import { useEffect, useState } from "react";
import { Loader2, Mail, LogOut, Download, FileText } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

const cred = { withCredentials: true };

function SignIn() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) { setMsg("Enter a valid email."); return; }
    setBusy(true);
    try { const { data } = await api.post("/auth/magic-link", { email }, cred); setMsg(data.message); }
    catch { setMsg("Something went wrong. Try again."); } finally { setBusy(false); }
  };
  return (
    <form noValidate onSubmit={submit} className="mt-10 max-w-md space-y-4" data-testid="account-signin-form">
      <p className="text-[color:var(--vt-body-on-cream)]">Enter the email you used to buy. We'll send a one-time sign-in link that expires in 15 minutes.</p>
      <input data-testid="account-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
        className="w-full bg-white border-2 border-[color:var(--vt-dark)] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--vt-amber)]" />
      <button data-testid="account-send-link" disabled={busy} className="flex items-center gap-2 px-5 py-3 bg-[color:var(--vt-dark)] text-[color:var(--vt-cream)] font-head uppercase hover:bg-[color:var(--vt-amber)] hover:text-[color:var(--vt-dark)] transition-colors disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Email me a sign-in link
      </button>
      {msg && <p className="text-sm text-[color:var(--vt-dark)]" data-testid="account-signin-msg" role="status">{msg}</p>}
    </form>
  );
}

function Section({ title, items, testid, empty }) {
  return (
    <div data-testid={testid}>
      <p className="font-head uppercase text-sm text-[color:var(--vt-dark)] mb-2">{title}</p>
      {items.length === 0 ? <p className="text-sm text-[color:var(--vt-graphite)]">{empty}</p> : (
        <div className="border-2 border-[color:var(--vt-dark)] divide-y divide-[color:var(--vt-dark)]/20 bg-white">
          {items.map((x, i) => (
            <a key={i} href={x.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3 hover:bg-[color:var(--vt-cream)] transition-colors">
              {x.icon || <FileText size={16} className="text-[color:var(--vt-amber)]" />}<span className="flex-1 text-sm">{x.title || x.name}</span>
              {x.gift && <span className="text-[10px] uppercase font-[IBM_Plex_Mono]">gift</span>}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Account() {
  const [me, setMe] = useState(undefined);
  const load = () => api.get("/account/me", cred).then((r) => setMe(r.data)).catch(() => setMe(null));
  useEffect(() => { load(); }, []);
  const logout = async () => { await api.post("/auth/logout", {}, cred); setMe(null); };

  return (
    <main className="max-w-3xl mx-auto px-5 py-16" data-testid="account-page">
      <Seo title="My Voluntold account" description="Sign in to re-download your kits and Quick Plays." />
      <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--vt-amber)] font-[IBM_Plex_Mono]">My account</p>
      <h1 className="font-head uppercase text-4xl sm:text-5xl text-[color:var(--vt-dark)] mt-2">{me ? `Welcome back${me.name ? `, ${me.name}` : ""}.` : "Your Voluntold stuff"}</h1>
      {me === undefined && <Loader2 className="animate-spin mt-8" />}
      {me === null && <SignIn />}
      {me && (
        <div className="mt-10 space-y-8" data-testid="account-dashboard">
          <div className="flex items-center gap-3 text-sm text-[color:var(--vt-body-on-cream)]">
            <span data-testid="account-signed-in-as">Signed in as {me.email}</span>
            <button data-testid="account-logout" onClick={logout} className="flex items-center gap-1 underline"><LogOut size={14} /> Sign out</button>
          </div>
          <Section title="Starter Kits" testid="account-kits" items={me.starter_kits} empty="No Starter Kits yet." />
          <Section title="Quick Plays" testid="account-quickplays" items={me.quick_plays} empty="No Quick Plays yet." />
          <Section title="Downloads" testid="account-downloads" empty="No downloads yet." items={me.downloads.map((d) => ({ ...d, icon: <Download size={16} className="text-[color:var(--vt-amber)]" /> }))} />
          {me.assessment_url && <a data-testid="account-assessment" href={me.assessment_url} className="inline-block underline text-sm">View my Coach Readiness results</a>}
          <p className="text-xs text-[color:var(--vt-graphite)]">Download links are fresh each time you sign in and expire after 24 hours.</p>
        </div>
      )}
    </main>
  );
}
